import { type Common } from './common.js';
import { type SuccessResponse } from './models/common.js';
import { type PaginatedQueryOptions } from './models/pagination/PaginatedQueryOptions.type.js';
import { type PaginationResponse } from './models/pagination/PaginationResponse.type.js';
import {
  type DisplayablePrevisitFormTemplate,
  type PrevisitFormTemplateWithParametersAndConfigs,
} from './models/previsit/formTemplate.js';
import type * as PrevisitFormTemplate from './models/previsit/formTemplate.js';
import {
  type BasePrevisitFormTemplateFieldConfig,
  type FindAllLabelsOptions,
  type PrevisitFormTemplateFieldConfig,
  type PrevisitFormTemplateFieldConfigWithConditions,
  type UpsertValues as UpsertFieldConfig,
} from './models/previsit/formTemplateFieldConfig.js';
import { type FindAllOptions } from './models/previsit.js';

export class API {
  common: Common;
  baseEndpoint: string;

  constructor(common: Common) {
    this.common = common;
    this.baseEndpoint = '/previsit/form-templates';
  }

  /**
   * @deprecated Use `findAll` instead
   */
  async fetchAll(
    options: FindAllOptions = {}
  ): Promise<{ previsitFormTemplates: DisplayablePrevisitFormTemplate[] }> {
    const url = this.common.constructPath(`${this.baseEndpoint}`, options);
    return await this.common.getJSON(url);
  }

  async findAll(options: FindAllOptions = {}): Promise<DisplayablePrevisitFormTemplate[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}`, options);
    const response = await this.common.getJSON(url);

    return response.previsitFormTemplates;
  }

  async findAllPaginated(
    options: PaginatedQueryOptions<FindAllOptions>
  ): Promise<PaginationResponse<DisplayablePrevisitFormTemplate>> {
    const url = this.common.constructPath(`${this.baseEndpoint}`, options);
    return await this.common.getJSON(url);
  }

  async createFormTemplate(
    createValues: PrevisitFormTemplate.CreateValues
  ): Promise<PrevisitFormTemplate.PrevisitFormTemplateWithLocalizations> {
    const url = this.common.constructPath(`${this.baseEndpoint}`);
    return await this.common.postJSON(url, createValues);
  }

  async createFormTemplatePreview(
    formTemplateId: string,
    templateFieldConfigs: BasePrevisitFormTemplateFieldConfig[]
  ): Promise<PrevisitFormTemplate.PrevisitFormTemplatePreview> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${formTemplateId}/preview`);
    return await this.common.postJSON(url, {
      fields: templateFieldConfigs,
    });
  }

  async updateFormTemplate(
    formTemplateId: string,
    updateValues: PrevisitFormTemplate.UpdateValues
  ): Promise<{
    template: PrevisitFormTemplate.PrevisitFormTemplateWithLocalizations;
  }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${formTemplateId}`);
    return await this.common.patchJSON(url, updateValues);
  }

  async createFormTemplateFieldConfigs(
    formTemplateId: string,
    templateFieldConfigs: BasePrevisitFormTemplateFieldConfig[]
  ): Promise<PrevisitFormTemplateFieldConfig[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${formTemplateId}/field-configs`);
    return await this.common.postJSON(url, {
      fields: templateFieldConfigs,
    });
  }

  async updateFormTemplateFieldConfig(
    formTemplateId: string,
    formTemplateFieldConfigId: string,
    updateValues: Partial<BasePrevisitFormTemplateFieldConfig>
  ): Promise<PrevisitFormTemplateFieldConfig> {
    const url = this.common.constructPath(
      `${this.baseEndpoint}/${formTemplateId}/field-configs/${formTemplateFieldConfigId}`
    );
    return await this.common.patchJSON(url, updateValues);
  }

  async updateFormTemplateFieldConfigs(
    formTemplateId: string,
    templateFieldConfigs: UpsertFieldConfig[]
  ): Promise<PrevisitFormTemplateFieldConfigWithConditions[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${formTemplateId}/field-configs`);
    return await this.common.putJSON(url, {
      fields: templateFieldConfigs,
    });
  }

  async deleteFormTemplate(id: string): Promise<SuccessResponse> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    return await this.common.deleteJSON(url);
  }

  async deleteFormTemplateLanguage(id: string, languageId: string): Promise<SuccessResponse> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}/languages/${languageId}`);
    return await this.common.deleteJSON(url);
  }

  async fetchAllLabels(options: FindAllLabelsOptions): Promise<string[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/labels`, options);
    return await this.common.getJSON(url);
  }

  async fetchPrevisitFormTemplate(
    formTemplateId: string,
    parseParameters?: boolean
  ): Promise<PrevisitFormTemplateWithParametersAndConfigs> {
    const url = this.common.constructPath(
      `${this.baseEndpoint}/${formTemplateId}`,
      parseParameters ? { parseParameters } : undefined
    );
    return await this.common.getJSON(url);
  }

  async findHistory(
    formTemplateId: string
  ): Promise<PrevisitFormTemplate.PrevisitFormTemplateHistory[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${formTemplateId}/history`);
    const { history } = await this.common.getJSON(url);
    return history;
  }
}
